"use client"
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react"

// ─── Editable content ────────────────────────────────────────────────────────
// One shared clock for every in-canvas hover takeover (policy, metric, power,
// capability): the replacement fades in over this many ms when it takes over
// its slot, and fades back out when the hover ends. The content underneath
// crossfades the opposite way on the same clock.
export const HOVER_SWAP_FADE_MS = 110
// Asymmetric easing: whatever is ARRIVING eases out (starts fast, lands
// gently); whatever is LEAVING eases in (slips away). Applies to both the
// takeover overlay and the content underneath, in both choreographies.
export const HOVER_SWAP_FADE_IN_EASING = "ease-out"
export const HOVER_SWAP_FADE_OUT_EASING = "ease-in"

// ─── Choreography: crossfade (default) vs sequential ─────────────────────────
// Crossfade: the original content and the takeover fade in opposite directions
// AT THE SAME TIME — one dissolve, HOVER_SWAP_FADE_MS long, both texts briefly
// overlapping at partial opacity.
// Sequential (<HoverSwapSequentialFade> around a HUD body): the outgoing side
// always finishes before the incoming side starts — content fully out, THEN
// the takeover in; takeover fully out, THEN the content back in. Nothing ever
// overlaps, at the cost of a 2×HOVER_SWAP_FADE_MS round trip.
const HoverSwapSequentialContext = createContext(false)

// Wrap a HUD variant's body to switch every swap target inside it (and the
// overlays) to the sequential choreography.
export const HoverSwapSequentialFade = ({ children }: { children: ReactNode }) => (
  <HoverSwapSequentialContext.Provider value={true}>
    {children}
  </HoverSwapSequentialContext.Provider>
)

// Read by every swap target so its hidden-content style and its overlay agree
// on the choreography.
export const useHoverSwapSequentialFade = () => useContext(HoverSwapSequentialContext)

// How far into the outgoing fade the incoming side starts, in the DEFAULT
// choreography: 0 = pure simultaneous crossfade (both texts overlap at half
// strength mid-swap, reads like a double exposure), 1 = fully sequential.
// A small stagger keeps the swap feeling like one motion while letting the
// outgoing side visibly clear first.
export const HOVER_SWAP_CROSSFADE_STAGGER = 0.4

// The incoming side's transition-delay: a stagger fraction of the fade in the
// default choreography; the full fade in sequential mode.
const enterDelayForMode = (sequential: boolean) =>
  sequential
    ? HOVER_SWAP_FADE_MS
    : Math.round(HOVER_SWAP_FADE_MS * HOVER_SWAP_CROSSFADE_STAGGER)

// Style for the swap target's ORIGINAL content while a takeover covers it:
// same opacity-hide as before (undefined when idle so an outer swap target's
// hiding still composes), now transitioned so it fades against the overlay.
// Pass the target's useHoverSwapSequentialFade() as `sequential` — fading OUT
// is always immediate; fading back IN waits for the overlay's exit first in
// sequential mode.
export const hoverSwapHiddenContentStyle = (
  hidden: boolean,
  sequential = false,
): CSSProperties => ({
  opacity: hidden ? 0 : undefined,
  transition: `opacity ${HOVER_SWAP_FADE_MS}ms ${hidden ? HOVER_SWAP_FADE_OUT_EASING : HOVER_SWAP_FADE_IN_EASING} ${hidden ? 0 : enterDelayForMode(sequential)}ms`,
})

// Same fade for content that must ALSO drop out of hit-testing while
// covered (the capability takeover's whole lower half, the labor strip):
// opacity carries the fade while visibility flips AFTER the fade-out (delayed
// transition) and back ON immediately when the hover ends.
export const hoverSwapHiddenAndUntargetableContentStyle = (
  hidden: boolean,
  sequential = false,
): CSSProperties => ({
  visibility: hidden ? "hidden" : undefined,
  opacity: hidden ? 0 : undefined,
  transition: `opacity ${HOVER_SWAP_FADE_MS}ms ${hidden ? HOVER_SWAP_FADE_OUT_EASING : HOVER_SWAP_FADE_IN_EASING} ${hidden ? 0 : enterDelayForMode(sequential)}ms, visibility 0s ${hidden ? HOVER_SWAP_FADE_MS : 0}ms`,
})

// When any overlay starts fading OUT, it stamps this shared clock; an overlay
// about to fade IN delays its entrance by whatever remains of that fade. Two
// takeovers trading places therefore run SEQUENTIALLY — the old one fully out,
// the new one then in — instead of crossfading over each other (the "bleed").
// Module-level on purpose: the trading overlays live in different components
// (the policy and metric details both overlay the treemap slot).
let lastFadeOutStartedAtMs = 0
const stampFadeOutStart = () => {
  lastFadeOutStartedAtMs = Date.now()
}
const remainingFadeOutMs = () =>
  Math.max(0, HOVER_SWAP_FADE_MS - (Date.now() - lastFadeOutStartedAtMs))

// Fade in/out wrapper for a hover-takeover overlay. While `active` the
// children render in a div that fades in; when `active` drops, the last
// active-frame children stay mounted and fade out, then unmount.
//
// `contentKey` is the identity of what's being shown (the hovered policy id,
// metric id, …). When it changes while active — hovering from one policy
// square to the next — the switch gets the SAME staggered treatment as a
// takeover replacing the underlying content: the old item starts fading out
// immediately and the new one enters on the choreography's delay, the two
// stacked in the same grid cell. Content updates under the SAME key render
// live with no refade (e.g. highlight changes inside the power breakdown as
// cells are crossed).
//
// The fade runs on a plain opacity TRANSITION in both directions (content
// commits at opacity 0, then `entered` flips on the next frame) rather than a
// mount keyframe: a keyframe animation would override the exit transition
// while still running, so leaving within the first HOVER_SWAP_FADE_MS would
// flash the overlay to full opacity before dropping it. With a single
// transition, quick enter/leave reversals retrace smoothly from the current
// opacity in either direction.
export const HoverSwapFadeOverlay = ({
  active,
  contentKey,
  className,
  style,
  children,
}: {
  active: boolean
  contentKey?: string
  className?: string
  style?: CSSProperties
  children: ReactNode
}) => {
  const sequential = useContext(HoverSwapSequentialContext)
  const targetKey = active ? (contentKey ?? "content") : null
  // The key whose content is currently entering/held; null = nothing.
  const [shownKey, setShownKey] = useState<string | null>(targetKey)
  // Chosen when the current content begins entering: the remainder of any
  // in-flight fade-out elsewhere, so this entrance starts after it finishes.
  const [enterDelayMs, setEnterDelayMs] = useState(0)
  const [entered, setEntered] = useState(false)
  // The previous item's content while an item→item switch fades it out,
  // stacked in the same grid cell as the incoming one. Its div keeps the OLD
  // key, so React moves the existing DOM node here and the fade-out continues
  // from its current opacity instead of restarting.
  const [exiting, setExiting] = useState<{ key: string; children: ReactNode } | null>(null)

  // Children snapshot for the shown key, refreshed during render while that
  // key is current (not an effect) so the very first fade-out frame already
  // has the content.
  const shownChildrenRef = useRef<ReactNode>(null)
  if (active && targetKey === shownKey) shownChildrenRef.current = children

  // Whole-overlay fade-out (the hover ended). Stamp the shared clock
  // render-time so sibling overlays entering in the same commit see it from
  // their effects.
  const fadingOutWhole = targetKey === null && shownKey !== null
  const wasFadingOut = useRef(false)
  if (fadingOutWhole && !wasFadingOut.current) stampFadeOutStart()
  wasFadingOut.current = fadingOutWhole

  // Hover ended: the content fades out in place (opacity 0 below), then
  // unmounts.
  useEffect(() => {
    if (!fadingOutWhole) return
    const timer = setTimeout(() => setShownKey(null), HOVER_SWAP_FADE_MS)
    return () => clearTimeout(timer)
  }, [fadingOutWhole])

  // Item → item switch while active: the old content moves to the exiting
  // slot (fading out immediately) and the new key enters on the staggered
  // clock — the same treatment as a takeover replacing underlying content.
  useEffect(() => {
    if (targetKey === null || shownKey === null || targetKey === shownKey) return
    setExiting({ key: shownKey, children: shownChildrenRef.current })
    setShownKey(targetKey)
    setEntered(false)
    setEnterDelayMs(enterDelayForMode(sequential))
  }, [targetKey, shownKey, sequential])

  // The exiting item unmounts once its fade is done.
  useEffect(() => {
    if (!exiting) return
    const timer = setTimeout(() => setExiting(null), HOVER_SWAP_FADE_MS)
    return () => clearTimeout(timer)
  }, [exiting])

  // Idle → active: show the target content straight away (delayed only by a
  // sibling overlay's in-flight fade-out, via enterDelayMs).
  useEffect(() => {
    if (shownKey === null && targetKey !== null) {
      setShownKey(targetKey)
      setEntered(false)
      setEnterDelayMs(remainingFadeOutMs())
    }
  }, [shownKey, targetKey])

  // `entered` flips one frame AFTER the content commits at opacity 0, so the
  // flip to 1 actually transitions. rAF is the "after the first painted
  // frame" signal; the timeout covers hidden tabs, where rAF never fires.
  const showing = shownKey !== null && shownKey === targetKey
  useEffect(() => {
    if (!showing) return
    let flipped = false
    const flip = () => {
      if (flipped) return
      flipped = true
      setEntered(true)
    }
    const raf = requestAnimationFrame(flip)
    const timer = setTimeout(flip, 50)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(timer)
    }
  }, [showing, shownKey])

  if (shownKey === null && exiting === null) return null
  const visible = showing && entered
  return (
    // display:grid stacks the incoming and exiting items in one cell. Callers
    // pass positioning-only classes; any layout for the content itself
    // belongs on an element INSIDE children, not on this root.
    <div className={className} style={{ ...style, display: "grid" }}>
      {exiting && exiting.key !== shownKey && (
        <div
          key={exiting.key}
          style={{
            gridArea: "1 / 1",
            opacity: 0,
            transition: `opacity ${HOVER_SWAP_FADE_MS}ms ${HOVER_SWAP_FADE_OUT_EASING}`,
          }}
        >
          {exiting.children}
        </div>
      )}
      {shownKey !== null && (
        <div
          key={shownKey}
          style={{
            gridArea: "1 / 1",
            opacity: visible ? 1 : 0,
            // Entering waits for a sibling overlay's in-flight fade-out
            // (enterDelayMs) — and always at least the choreography's stagger
            // (full fade in sequential mode) for whatever else is leaving.
            transition: `opacity ${HOVER_SWAP_FADE_MS}ms ${visible ? HOVER_SWAP_FADE_IN_EASING : HOVER_SWAP_FADE_OUT_EASING} ${visible ? Math.max(enterDelayMs, enterDelayForMode(sequential)) : 0}ms`,
          }}
        >
          {showing && active ? children : shownChildrenRef.current}
        </div>
      )}
    </div>
  )
}
